import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { favoriteService } from '../../services/favoriteService';
import { supabase } from '../../lib/supabase';

/**
 * FavoriteButton Component
 * A reusable heart icon button with optimistic UI updates
 * 
 * @param {string} itemId - ID of the item to favorite
 * @param {string} itemType - Type: 'event', 'spot', 'product', 'marketplace_item'
 * @param {string} className - Additional CSS classes
 */
const FavoriteButton = ({ itemId, itemType, className = '' }) => {
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check if item is favorited on mount
  useEffect(() => {
    checkFavoriteStatus();
  }, [itemId]);

  const checkFavoriteStatus = async () => {
    try {
      const favorited = await favoriteService?.checkIfFavorite(itemId);
      setIsFavorited(favorited);
    } catch (error) {
      console.error('Error checking favorite status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (e) => {
    e?.stopPropagation(); // Prevent triggering parent click events
    e?.preventDefault();  // Prevent default action

    // Check if user is logged in
    const { data: { user }, error } = await supabase?.auth?.getUser();
    if (error || !user) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    // OPTIMISTIC UI UPDATE: Toggle state IMMEDIATELY
    const previousState = isFavorited;
    setIsFavorited(!isFavorited);

    try {
      // Call API in background
      const isNowFavorited = await favoriteService?.toggleFavorite(itemId, itemType);
      // API returns boolean directly, not object
      setIsFavorited(isNowFavorited);
    } catch (error) {
      // REVERT on error
      setIsFavorited(previousState);
      console.error('Error toggling favorite:', error);
      
      // Show error message to user
      if (error?.message === 'Giriş yapmanız gerekiyor' || error?.message === 'Giriş yapmalısınız') {
        navigate('/login');
      }
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={isLoading}
      className={`absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-card/90 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all duration-200 shadow-lg ${className}`}
      aria-label={isFavorited ? 'Favorilerden çıkar' : 'Favorilere ekle'}
    >
      <Icon
        name="Heart"
        size={18}
        className={`transition-all duration-200 ${
          isFavorited ? 'fill-current text-error' : 'text-foreground'
        }`}
      />
    </button>
  );
};

export default FavoriteButton;